import Pagination from 'rc-pagination';
import React, { useEffect } from 'react';
import enUsLocale from 'rc-pagination/lib/locale/en_US';
import { css } from '@linaria/core';
import { NavigatePrev, NavigateNext, Ellipsis } from '../icons';
import { trackPaginationSelection } from '../utils/dataLayer';

const wrapper = css`
  .rc-pagination {
    display: flex;
    align-items: center;
    list-style: none;
    margin-bottom: 24px;
  }

  .rc-pagination-prev,
  .rc-pagination-next {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: 1px solid #1a1a1a;

    @media (min-width: 800px) {
      width: 50px;
      height: 50px;
    }

    &.rc-pagination-disabled {
      border-color: #1a1a1a80;

      a {
        box-shadow: none;
        cursor: not-allowed;
      }

      svg {
        fill: #1a1a1a80;
        cursor: not-allowed;
      }
    }

    &:not(.rc-pagination-disabled):hover {
      color: #fff;
      background-color: #1a1a1a;

      svg {
        fill: #fff;
      }
    }

    .rc-pagination-link {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      width: 100%;

      &:focus {
        background-color: transparent;
      }
    }

    &:not(.rc-pagination-disabled):focus-within {
      background-color: #1a1a1a;
      border-color: #1a1a1a;
      outline: 3px solid #ffdd00;

      svg {
        fill: #fff;
      }
    }

    .rc-pagination-link:focus-visible {
      outline: none;
      background-color: transparent;
    }
  }

  .rc-pagination-prev {
    margin-right: 10px;

    @media (min-width: 800px) {
      margin-right: 30px;
    }
  }

  .rc-pagination-next {
    margin-left: 10px;

    @media (min-width: 800px) {
      margin-left: 30px;
    }
  }

  .rc-pagination-item {
    cursor: pointer;
    font-size: 18px;
    line-height: 20px;
    margin: 10px;
    padding: 1px;
    color: #1a1a1a;
    text-decoration-thickness: 1px;
    text-underline-offset: 2px;
    transition: color 300ms ease-in-out;

    &.rc-pagination-item-active {
      text-decoration: none !important;

      a {
        font-weight: 400;
        text-decoration: none !important;
      }
    }

    &:hover {
      color: #1a1a1a;
      text-decoration-thickness: 2px;
      text-underline-offset: 2px;
    }
  }

  .rc-pagination-link {
    text-decoration-thickness: 1px;

    &:hover {
      text-decoration-thickness: 2px;
    }

    &:focus {
      text-decoration: none;
    }
  }

  .rc-pagination-jump-prev,
  .rc-pagination-jump-next {
    cursor: pointer;
    padding: 1px;
    margin: 9px;
    line-height: 20px;

    svg {
      position: relative;
      top: 6px;
    }
  }
`;

const jumpIcon = css`
  fill: #1a1a1a;
  width: 15px;
  height: 15px;
  vertical-align: unset;
`;
const navigateIcon = css`
  fill: #1a1a1a;
  width: 16px;
  height: 16px;
  vertical-align: unset;

  @media (min-width: 800px) {
    width: 25px;
    height: 25px;
  }
`;

export default function Paging({
  current,
  resultsPerPage,
  onChange,
  totalPages,
  effectiveTotalPages,
  ...rest
}) {
  const pageCount = effectiveTotalPages ?? totalPages;

  const itemRender = (page, type, element) => {
    const isPrev = type === 'prev';
    const isNext = type === 'next';
    const isJumpPrev = type === 'jump-prev';
    const isJumpNext = type === 'jump-next';

    const disabledFromProps = element.props.className?.includes(
      'rc-pagination-disabled',
    );

    const clickHandler = e => {
      e.preventDefault();

      const fromDom =
        e.currentTarget instanceof Element
          ? !!e.currentTarget
              .closest('li')
              ?.classList.contains('rc-pagination-disabled')
          : false;
      const isDisabled = disabledFromProps || fromDom;

      if (!isDisabled) {
        handleChange(page);
      }
    };

    const keyboardHandler = e => {
      const key = e.key || e.code;

      if (
        key === 'Enter' ||
        key === ' ' ||
        key === 'Spacebar' ||
        key === 'Space'
      ) {
        e.preventDefault();

        const fromDom =
          e.currentTarget instanceof Element
            ? !!e.currentTarget
                .closest('li')
                ?.classList.contains('rc-pagination-disabled')
            : false;
        const isDisabled = disabledFromProps || fromDom;

        if (!isDisabled) {
          handleChange(page);
        }
      }
    };

    const getQueryParamsString = () => {
      const params = new URLSearchParams(window.location.search);
      params.set('current', `n_${page}_n`);
      return '?' + params.toString();
    };

    if (isPrev || isNext || isJumpPrev || isJumpNext) {
      const ariaLabel =
        (isPrev && 'Previous page') ||
        (isNext && 'Next page') ||
        (isJumpPrev && 'Jump to previous 5 pages') ||
        (isJumpNext && 'Jump to next 5 pages') ||
        '';

      // Custom <a> for Navigational items for accessibility and SEO.
      return (
        <a
          aria-disabled={disabledFromProps ? 'true' : undefined}
          aria-label={ariaLabel}
          className="rc-pagination-link"
          href={disabledFromProps ? undefined : getQueryParamsString()}
          onClick={clickHandler}
          onKeyDown={keyboardHandler}
          rel={isPrev ? 'prev' : isNext ? 'next' : undefined}
          tabIndex={disabledFromProps ? -1 : 0}
        >
          {isPrev && <NavigatePrev className={navigateIcon} />}
          {isNext && <NavigateNext className={navigateIcon} />}
          {isJumpPrev && <Ellipsis className={jumpIcon} />}
          {isJumpNext && <Ellipsis className={jumpIcon} />}
        </a>
      );
    }

    // Numbered page items
    return React.cloneElement(element, {
      'aria-label': `Page ${page}`,
      className: 'rc-pagination-link',
      href: getQueryParamsString(),
      role: element.props?.role ?? 'link',
      tabIndex: 0,
      onClick: clickHandler,
      onKeyDown: keyboardHandler,
    });
  };

  useEffect(() => {
    // Remove tabindex from <li> added by rc-pagination to stop double tabbing
    const removeTabIndexFromLIs = () => {
      document.querySelectorAll('.rc-pagination li[tabindex]').forEach(li => {
        li.removeAttribute('tabindex');
      });
    };

    // Ensure <a> inside disabled <li> are not focusable and receive styling
    const applyAnchorTabindexFromParent = () => {
      document
        .querySelectorAll(
          '.rc-pagination li.rc-pagination-disabled a, .rc-pagination li.rc-pagination-disabled .rc-pagination-link',
        )
        .forEach(a => {
          if (a instanceof Element) {
            a.setAttribute('tabindex', '-1');
            a.setAttribute('aria-disabled', 'true');
          }
        });
    };

    removeTabIndexFromLIs();
    applyAnchorTabindexFromParent();
  }, [current, resultsPerPage, totalPages, effectiveTotalPages]);

  const handleChange = (...args) => {
    let top = 0;
    const scrollTarget = document.querySelector('div[data-layout-mode]');

    if (scrollTarget instanceof Element) {
      top = scrollTarget.offsetTop;
    }

    window.scrollTo({ top: top, behavior: 'smooth' });

    onChange(...args);
    trackPaginationSelection(args[0]);
  };

  return (
    <div className={wrapper}>
      <Pagination
        current={current}
        itemRender={itemRender}
        locale={enUsLocale}
        onChange={handleChange}
        pageSize={resultsPerPage}
        total={pageCount * resultsPerPage}
        {...rest}
      />
    </div>
  );
}
